import _regeneratorRuntime from '@babel/runtime/regenerator';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _asyncToGenerator from '@babel/runtime/helpers/esm/asyncToGenerator';
import { create } from 'apisauce';
import { ValidationError, object, string, date, number, boolean, array } from 'yup';

var baseStaticConfig = {
  defaultHeaders: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'x-bpi-service-context': 'USER',
    'X-TS-AJAX-Request': true
  }
};

/** @param {number} value */
var fix = function fix(value) {
  return ("0" + value).slice(-2);
};
/**
 * @param {Date} date
 */


var formatDate = function formatDate(date) {
  return date.getFullYear() + "-" + fix(date.getMonth() + 1) + "-" + fix(date.getDate()) + "T" + fix(date.getHours()) + ":" + fix(date.getMinutes()) + ":" + fix(date.getSeconds());
};
/**
 * @param {string} appId
 * @param {string | null} [userTrackingId]
 */


var setClientContext = function setClientContext(appId, userTrackingId) {
  if (!appId) {
    throw Error("appId is required. Value received: " + appId);
  }

  return JSON.stringify({
    client: {
      user_tracking_id: userTrackingId || '',
      app_title: appId,
      app_package_name: 'WEB'
    },
    env: {
      platform: 'WEB',
      // @ts-ignore
      platform_version: navigator.appVersion || navigator.vendor || window.opera,
      // @ts-ignore
      make: navigator.platform || window.opera,
      locale: navigator.language
    }
  });
};
/**
 * @param {string} clientContext
 * @param {import('types').BankId} [bankId]
 * @param {string} [customerNumber]
 * @param {number} [apiTrustLevel]
 * @param {string} [apiTrustLevelCreatedDate]
 */

var setStaffHeaders = function setStaffHeaders(clientContext, bankId, customerNumber, apiTrustLevel, apiTrustLevelCreatedDate) {
  return {
    'x-bpi-client-context': clientContext,
    'x-bpi-customer-id': customerNumber,
    'x-bpi-trust-level': apiTrustLevel,
    'x-bpi-trust-created-dt': apiTrustLevelCreatedDate || formatDate(new Date()),
    'x-bpi-customer-bank-id': bankId
  };
};
/**
 * @param {string} clientContext
 */

var setUserHeaders = function setUserHeaders(clientContext) {
  return {
    'x-bpi-client-context': clientContext
  };
};
/**
 * Add appversion props in client context object of headers
 * @param {import('apisauce').HEADERS} headers
 */

var setAppVersionHeaders = function setAppVersionHeaders(headers) {
  var newHeaders = _objectSpread({}, headers);

  var clientContext = JSON.parse(newHeaders['x-bpi-client-context']);
  clientContext.client = _objectSpread(_objectSpread({}, clientContext.client), {}, {
    app_version_code: navigator.appVersion,
    app_version_name: navigator.appName
  });
  newHeaders['x-bpi-client-context'] = JSON.stringify(clientContext);
  return newHeaders;
};

/**
 * @param {import('types').BankId} bankId
 * @param {string | undefined} [appTitle] // During a Registration flow this value comes as IBTBREG CB or IBTBREG YB etc
 */

var getAppIdFromBankId = function getAppIdFromBankId(bankId, appTitle) {
  if (appTitle === void 0) {
    appTitle = undefined;
  }

  return appTitle || bankId + " Web";
};
/**
 * Init our main API connector. These are transient and will be instantiated as required.
 *
 * The microservices require standard header format so we take care of that for each one created. There are 2 types of header
 * format staff and user.
 *
 * @param {string} baseUrl Base URL for the API.
 * @param {import('types').BankId} bankId - a string of 'CB' || 'YB' this is added as a global prop of apisauce and referenced in each endpoint url
 * @param {string | null} [userTrackingId]
 * @param {string | undefined} [appTitle]
 * @returns {import('apisauce').ApisauceInstance}
 */


var initApiConnector = function initApiConnector(baseUrl, bankId, userTrackingId, appTitle) {
  var appId = getAppIdFromBankId(bankId, appTitle);
  var clientContext = setClientContext(appId, userTrackingId);
  var dynamicHeaders = setUserHeaders(clientContext); // todo check these headers are correct

  return create({
    baseURL: baseUrl,
    headers: _objectSpread(_objectSpread({}, baseStaticConfig.defaultHeaders), dynamicHeaders)
  });
};
/**
 * Init our non branded API connector. These are transient and will be instantiated as required.
 *
 * The microservices require standard header format so we take care of that for each one created. This is brand agnostic
 *
 * @param {string} baseUrl Base URL for the API.
 * @param {string} appId Identifier for this application.
 * @param {string | null} [userTrackingId]
 * @returns {import('apisauce').ApisauceInstance}
 */

var initNoBankIdApiConnector = function initNoBankIdApiConnector(baseUrl, appId, userTrackingId) {
  var clientContext = setClientContext(appId, userTrackingId);
  var dynamicHeaders = setUserHeaders(clientContext); // todo check these headers are correct

  return create({
    baseURL: baseUrl,
    headers: _objectSpread(_objectSpread({}, baseStaticConfig.defaultHeaders), dynamicHeaders)
  });
};
/**
 * Init our staff only API connector. These are transient and will be instantiated as required.
 *
 * The microservices require standard header format so we take care of that for each one created.
 *
 * @param {string} baseUrl - a string for Base Url
 * @param {import('types').BankId} bankId - a string of 'CB' || 'YB' this is added as a global prop of apisauce and referenced in each endpoint url
 * @param {string} customerNumber - a string for customer number
 * @param {number} apiTrustLevel - a number for api trust level
 * @param {string | null} [userTrackingId] - a string for user tracking
 * @returns {import('apisauce').ApisauceInstance}
 */

var initApiConnectorStaffOnly = function initApiConnectorStaffOnly(baseUrl, bankId, customerNumber, apiTrustLevel, userTrackingId) {
  if (userTrackingId === void 0) {
    userTrackingId = null;
  }

  var appId = getAppIdFromBankId(bankId);
  var clientContext = setClientContext(appId, userTrackingId);
  var dynamicHeaders = setStaffHeaders(clientContext, bankId, customerNumber, apiTrustLevel);
  return create({
    baseURL: baseUrl,
    headers: _objectSpread(_objectSpread({}, baseStaticConfig.defaultHeaders), dynamicHeaders)
  });
};
/**
 * This function is currently a placeholder for returning GP Wallet App IDs
 * @returns {string}
 */

var getWalletAppId = function getWalletAppId() {
  return 'GP 1.0 Wallet Web';
};
/**
 * This is has been adapted strictly for GP Wallet APIs
 *
 * @param {string} baseUrl Base URL for the API.
 * @returns {import('apisauce').ApisauceInstance}
 */

var initGPApiConnector = function initGPApiConnector(baseUrl) {
  var appId = getWalletAppId();
  var clientContext = setClientContext(appId);
  var dynamicHeaders = setUserHeaders(clientContext); // todo check these headers are correct

  return create({
    baseURL: baseUrl,
    headers: _objectSpread(_objectSpread({}, baseStaticConfig.defaultHeaders), dynamicHeaders)
  });
};

var log = {
  /** @param {any} error */
  // eslint-disable-next-line no-console,
  logError: function logError(error) {
    return console.error(error);
  },

  /** @param {string} customMessage */
  // eslint-disable-next-line no-console,
  logWarn: function logWarn(customMessage) {
    return console.warn(customMessage);
  }
};

// from swagger docs
var guidRegex = /^([a-fA-F0-9]{8}(-[a-fA-F0-9]{4}){3}-[a-fA-F0-9]{12})$/;
var httpsRegex = /^https:(\/){2}/;
var decisionReferenceRegex = /^([a-fA-F0-9-]{1,15})$/;

/** @type {import('yup').TestOptions} */

var isGuid = {
  // eslint-disable-next-line no-template-curly-in-string
  message: '${path} must be a valid GUID',
  test: function test(value) {
    return value ? guidRegex.test(value) : false;
  }
};
/** @type {import('yup').TestOptions} */

var isHttps = {
  message: // eslint-disable-next-line no-template-curly-in-string
  '${path} must be a valid uri with a scheme matching the https pattern',
  test: function test(value) {
    return value ? httpsRegex.test(value) : false;
  }
};
/** @type {import('yup').TestOptions} */

var isDecisionReference = {
  // eslint-disable-next-line no-template-curly-in-string
  message: '${path} must have a valid Decision Reference ',
  test: function test(value) {
    return value ? decisionReferenceRegex.test(value) : false;
  }
};

var API_REQUEST_ERROR = 'Something went wrong while calling endpoint, Please check error logs';
var GENERIC_VALIDATION_ERROR = 'Generic validation error';

/**
 * @param {import('yup').Schema<T>} schema
 * @param {T} model
 * @returns {string | import('yup').ValidationError}
 * @template T
 */

function validate(schema, model) {
  try {
    schema.validateSync(model, {
      abortEarly: true
    });
    return '';
  } catch (error) {
    if (typeof error === 'string' || error instanceof ValidationError) {
      return error;
    } // Edge case


    return GENERIC_VALIDATION_ERROR;
  }
}

var EWalletStatus = ['PENDING', 'ACTIVE', 'PENDING_CLOSE', 'CLOSED'];
var customer = object({
  firstName: string().required(),
  lastName: string().required(),
  dateOfBirth: date().required(),
  aliasName: string()
});
var contact = object({
  email: string().required().email(),
  mobileCountryCode: string().required(),
  mobileNumber: string().required()
});
var termsAndConditions = object({
  termsAndConditionsId: string().required(),
  termsAndConditionsVersion: string().required(),
  acceptanceDateTime: date().required()
});
var linkedProducts = object({
  vRedId: string().test(isGuid),
  vAtlanticId: string().test(isGuid)
});
/**
 * @param {import('src/services/gp-wallet/methods').CreateWalletReqStub} createWalletReqStub} createWallet
 */

var validateCreateWalletDetails = function validateCreateWalletDetails(createWalletReqStub) {
  var createWalletSchema = object({
    customer: customer,
    contact: contact,
    termsAndConditions: termsAndConditions,
    linkedProducts: linkedProducts
  });
  return validate(createWalletSchema, createWalletReqStub);
};
/**
 * @param {string} walletId
 */

var validateWalletId = function validateWalletId(walletId) {
  var walletIdSchema = string().required().test(isGuid);
  return validate(walletIdSchema, walletId);
};
/**
 * @param {import('types').UpdateWalletReqStub} updateWalletReqStub
 */

var validateUpdateWalletReqStub = function validateUpdateWalletReqStub(updateWalletReqStub) {
  var updateWalletStatusSchema = object({
    walletStatus: string().oneOf(EWalletStatus).required()
  });
  return validate(updateWalletStatusSchema, updateWalletReqStub);
};
/**
 * @param {import('types').UpdateWalletCustomerStub} updateWalletCustomerReqStub
 */

var validateUpdateWalletCustomerStub = function validateUpdateWalletCustomerStub(updateWalletCustomerReqStub) {
  var updateWalletCustomerSchema = object({
    firstName: string().required(),
    lastName: string().required(),
    dateOfBirth: date().required(),
    aliasName: string().nullable()
  });
  return validate(updateWalletCustomerSchema, updateWalletCustomerReqStub);
};
/**
 * @param {import('types').UpdateWalletContactStub} updateWalletContactReqStub
 */

var validateUpdateWalletContactStub = function validateUpdateWalletContactStub(updateWalletContactReqStub) {
  var updateWalletCustomerSchema = object({
    email: string().email().required(),
    mobileCountryCode: string().required(),
    mobileNumber: number().required()
  });
  return validate(updateWalletCustomerSchema, updateWalletContactReqStub);
};
/**
 * @param {import('types').UpdateWalletTermsAndConditionsStub} updateWalletTermsAndConditionsStub
 */

var validateUpdateWalletTermsAndConditionsStub = function validateUpdateWalletTermsAndConditionsStub(updateWalletTermsAndConditionsStub) {
  var updateWalletTermsAndConditionsSchema = object({
    termsAndConditionsId: string().required(),
    termsAndConditionsVersion: string().required(),
    acceptanceDateTime: date().required()
  });
  return validate(updateWalletTermsAndConditionsSchema, updateWalletTermsAndConditionsStub);
};
/**
 * @param {import('types').UpdateWalletLinkedProductsStub} updateWalletLinkedProductsReqStub
 */

var validateUpdateWalletLinkedProductsStub = function validateUpdateWalletLinkedProductsStub(updateWalletLinkedProductsReqStub) {
  var updateWalletLinkedProductsSchema = object({
    vRedId: string().test(isGuid),
    vAtlanticId: string().test(isGuid)
  });
  return validate(updateWalletLinkedProductsSchema, updateWalletLinkedProductsReqStub);
};

var payMethodTypes = ['CARD', 'OB', 'BNPL', 'POINTS'];
var schemeType = ['AMEX', 'VISA', 'MASTERCARD', 'DINERS', 'DISCOVER', 'JCB'];
var cardPaymentType = ['CREDIT', 'DEBIT'];
var cardStatus = ['ACTIVE', 'EXPIRED', 'INACTIVE', 'REMOVED'];
var yBillingAddress = object({
  line1: string().required(),
  line2: string().required(),
  line3: string().required(),
  city: string().required(),
  postalCode: string().required(),
  county: string().required(),
  country: string().required()
});
var yCard = object({
  aliasName: string().nullable(),
  cardHolderName: string().required(),
  maskedPan: string().required(),
  expiry: string().required(),
  cardPriorityOrder: number().nullable(),
  cardPaymentToken: string().required(),
  cardPaymentSrd: string().required(),
  schemeType: string().oneOf(schemeType).required(),
  issuerName: string().nullable(),
  cardPaymentType: string().oneOf(cardPaymentType).required(),
  cardStatus: string().oneOf(cardStatus).required(),
  issuerArtRef: string().nullable(),
  cardArtRef: string().nullable(),
  isVmCard: boolean().required(),
  vmCardId: string().test(isGuid).nullable()
});
var yCreatePayCardPayload = object({
  billingAddress: yBillingAddress,
  aliasName: string().required(),
  cardHolderName: string().required(),
  pan: string().required(),
  expiry: string().required(),
  securityCode: string().required(),
  cardPriorityOrder: number().required()
});
var yPaymentMethod = object({
  walletId: string().test(isGuid),
  paymentMethodId: string().test(isGuid),
  paymentMethodType: string().oneOf(payMethodTypes).required(),
  createdDateTime: date().required(),
  updatedDateTime: date().required(),
  lastUsedDateTime: date().required(),
  billingAddress: yBillingAddress,
  card: yCard
});
var yPaymentMethodReqResStub = object({
  paymentMethod: yPaymentMethod
});
/**
 * @param {import('types').TCreatePayCardPayload} createPayMethodCardPayload
 */

var validatePayMethodCardPayload = function validatePayMethodCardPayload(createPayMethodCardPayload) {
  var createPayMethodCardSchema = yCreatePayCardPayload;
  return validate(createPayMethodCardSchema, createPayMethodCardPayload);
};
var yCardUpdatePayload = object({
  aliasName: string(),
  cardPriorityOrder: number().required(),
  cardStatus: string().oneOf(cardStatus).required()
});
/**
 * @param {import('types').TCardUpdatePayload} updatePayMethodCardPayload
 */

var validateUpdatePayMethodCardPayload = function validateUpdatePayMethodCardPayload(updatePayMethodCardPayload) {
  var updatePayMethodCardSchema = yCardUpdatePayload;
  return validate(updatePayMethodCardSchema, updatePayMethodCardPayload);
};
/**
 * @param {import('types').TBillingAddress} updatePayCardBillingAddPayload
 */

var validateUpdatePayCardBillingAddPayload = function validateUpdatePayCardBillingAddPayload(updatePayCardBillingAddPayload) {
  var updatePayCardBillingAddSchema = yBillingAddress;
  return validate(updatePayCardBillingAddSchema, updatePayCardBillingAddPayload);
};

/**
 * @param {string} vpayId
 */

var validateVpayId = function validateVpayId(vpayId) {
  var vpayIdSchema = string().required().test(isGuid);
  return validate(vpayIdSchema, vpayId);
};

var getUuidSchema = function getUuidSchema() {
  return string().required().test(isGuid);
};
/**
 *
 * @param {import('types').BankId} bankId
 */


var validateBankId = function validateBankId(bankId) {
  var bankIdSchema = string().required().oneOf(['CB', 'YB']).label('Bank Id');
  return validate(bankIdSchema, bankId);
};
/** @param {string} type */

var validateAccountsScopeId = function validateAccountsScopeId(type) {
  var typeSchema = string().required().oneOf(['User', 'Customer']).label("Url parameter 'type'");
  return validate(typeSchema, type);
};
var isLocalhost = /^http(s)?:\/\/localhost:\d+/;
/** @param {string} apiUrl */

var validateApiUrl = function validateApiUrl(apiUrl) {
  if (isLocalhost.test(apiUrl)) {
    return '';
  }

  var urlSchema = string().required().url().test(isHttps);
  var apiUrlSchema = urlSchema.label('Url');
  return validate(apiUrlSchema, apiUrl);
};
/** @param {string} uuid */

var validateAccessToken = function validateAccessToken(uuid) {
  var accessTokenSchema = getUuidSchema().label('Access token');
  return validate(accessTokenSchema, uuid);
};
/** @param {string} uuid */

var validateUserTrackingId = function validateUserTrackingId(uuid) {
  var trackingIdSchema = getUuidSchema().label('User tracking id');
  return validate(trackingIdSchema, uuid);
};
/** @param {string} type */

var validateBenType = function validateBenType(type) {
  var benTypeSchema = string().required().oneOf(['ALL', 'UK', 'IFT']).label('Beneficiary type');
  return validate(benTypeSchema, type);
};
/** @param {string} id */

var validateDecisionReference = function validateDecisionReference(id) {
  var decisionReferenceSchema = string().required().test(isDecisionReference).label('Decision reference');
  return validate(decisionReferenceSchema, id);
};

/**
 * @param {string | import('yup').ValidationError} error
 * @returns {error is import('yup').ValidationError}
 */

function isError(error) {
  return error !== '';
}
/**
 * @param {Array<string | import('yup').ValidationError>} errors
 */


function filterErrors(errors) {
  return errors.filter(isError);
}
/**
 * @param {Array<string | import('yup').ValidationError>} errors
 */


function handleErrorLogging(errors) {
  var parsedErrors = filterErrors(errors);

  if (parsedErrors.length > 0) {
    console.group('API Connector: Validation Error');
    parsedErrors.forEach(function (error) {
      console.group(error.name + ": " + error.message);
      console.log("value: " + JSON.stringify(error.value));
      console.groupCollapsed('Stack');
      console.error(error);
      console.groupEnd();
      console.groupEnd();
    });
    console.groupEnd();
  }
}
/**
 * @param {Array<string | import('yup').ValidationError>} errors
 */


function getErrorResponse(errors) {
  var filtered = filterErrors(errors);
  return filtered.length > 0 ? {
    error: filtered.join(',\n')
  } : {
    error: false
  };
}
/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param  {...string | import('yup').ValidationError} otherErrors
 */


var validateCommonMethodParams = function validateCommonMethodParams(baseUrl, bankId, token) {
  for (var _len = arguments.length, otherErrors = new Array(_len > 3 ? _len - 3 : 0), _key = 3; _key < _len; _key++) {
    otherErrors[_key - 3] = arguments[_key];
  }

  var errors = [validateApiUrl(baseUrl), validateBankId(bankId), validateAccessToken(token)].concat(otherErrors);
  handleErrorLogging(errors);
  return getErrorResponse(errors);
};
/**
 * @param {import('types').TWalletMethodParams} walletParams
 * * @param  {...string | import('yup').ValidationError} otherErrors
 */

var validateGPMethodParams = function validateGPMethodParams(_ref) {
  var baseUrl = _ref.baseUrl,
      token = _ref.token,
      _ref$vpayId = _ref.vpayId,
      vpayId = _ref$vpayId === void 0 ? '' : _ref$vpayId;

  for (var _len2 = arguments.length, otherErrors = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
    otherErrors[_key2 - 1] = arguments[_key2];
  }

  var errors = vpayId ? [validateApiUrl(baseUrl), validateAccessToken(token), validateVpayId(vpayId)].concat(otherErrors) : [validateApiUrl(baseUrl), validateAccessToken(token)].concat(otherErrors);
  handleErrorLogging(errors);
  return getErrorResponse(errors);
};
/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param  {...string | import('yup').ValidationError} otherErrors
 */

var validateParamsExceptToken = function validateParamsExceptToken(baseUrl, bankId) {
  for (var _len3 = arguments.length, otherErrors = new Array(_len3 > 2 ? _len3 - 2 : 0), _key3 = 2; _key3 < _len3; _key3++) {
    otherErrors[_key3 - 2] = arguments[_key3];
  }

  var errors = [validateApiUrl(baseUrl), validateBankId(bankId)].concat(otherErrors);
  handleErrorLogging(errors);
  return getErrorResponse(errors);
};
/**
 * @param {string} baseUrl
 * @param  {...string} otherErrors
 */

var validateNoBrandCommonMethodParams = function validateNoBrandCommonMethodParams(baseUrl) {
  for (var _len4 = arguments.length, otherErrors = new Array(_len4 > 1 ? _len4 - 1 : 0), _key4 = 1; _key4 < _len4; _key4++) {
    otherErrors[_key4 - 1] = arguments[_key4];
  }

  var errors = [validateApiUrl(baseUrl)].concat(otherErrors);
  handleErrorLogging(errors);
  return getErrorResponse(errors);
};

var getAccountsListPath="/banks/:bankId/accounts/:scope?type=:type";var getAccountDetailsPath="/banks/:bankId/accounts/:scope/:accountId";

/**
 * @param {import('apisauce').ApiErrorResponse<T>} response
 * @template T
 */

function parseError(response) {
  return {
    isError: true,
    error: response.problem || API_REQUEST_ERROR,
    status: response.status,
    response: response
  };
}
/**
 * @param {unknown} err
 * @returns {{ error: string }}
 */

var endpointError = function endpointError(err) {
  return {
    error: err instanceof Error ? err.message : API_REQUEST_ERROR
  };
};

/**
 * @param {import("apisauce").ApiResponse<T>} response
 * @template T
 */

function parseResponse(response) {
  if (response.ok) {
    /**
     * Enforce `null` for falsy values.
     *
     * Introduced by "nullish coalescing" in
     * apisauce v3.1.1.
     * @see https://github.com/infinitered/apisauce/pull/330
     */
    return response.data || null;
  }

  return parseError(response);
}

var matcher = /:[A-Za-z]+/g;
/**
 * @typedef {{ [key: string]: string | number | boolean }} DataValues
 */

/**
 * @param {string} key
 * @param {DataValues} values
 * @returns {string}
 */

function getValueByKey(key, values) {
  if (process.env.NODE_ENV !== 'production') {
    if (!values.hasOwnProperty(key)) {
      throw Error("URL substitution: key '" + key + "' not found");
    }
  }

  return "" + values[key];
}
/**
 * Replaces the keys in the URL with the specified values
 * @param {string} url
 * @param {DataValues} [values]
 */


var buildUrl = function buildUrl(url, values) {
  if (!values) {
    return url;
  }

  return url.replace(matcher, function (key) {
    return getValueByKey(key.substr(1), values);
  });
};

/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} type must be User || Customer
 */

var getAccountList = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, type, token) {
    var validations, apic, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateAccountsScopeId(type));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            apic.setHeaders(_objectSpread(_objectSpread({}, apic.headers), {}, {
              Authorization: "Bearer " + token
            }));
            path = buildUrl(getAccountsListPath, {
              bankId: bankId,
              scope: 'default',
              type: type
            });
            _context.next = 10;
            return apic.get(path);

          case 10:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 14:
            _context.prev = 14;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 18:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 14]]);
  }));

  return function getAccountList(_x, _x2, _x3, _x4) {
    return _ref.apply(this, arguments);
  };
}();

var X_0_8_0 = '0.8.0';
var X_1_0_0 = '1.0.0';
var X_1_2_0 = '1.2.0';
var X_1_1_0 = '1.1.0';

/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} userTrackingId
 * @param {string} accountId
 */

var getAccountDetails = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, accountId, userTrackingId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_0_8_0,
              Authorization: "Bearer " + token
            }));
            path = buildUrl(getAccountDetailsPath, {
              bankId: bankId,
              scope: 'default',
              type: false,
              accountId: accountId
            });
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function getAccountDetails(_x, _x2, _x3, _x4, _x5) {
    return _ref.apply(this, arguments);
  };
}();

var index = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getAccountList: getAccountList,
  getAccountDetails: getAccountDetails
});

var addressSearch="/ref/address/search/:postCode";var addressSearchById="/ref/address/:addressId";

/**
 * @typedef {Object} AddressSearchResult
 * @property {string} id
 * @property {string} potential_address
 */

/**
 * @param {{ short_addresses: Array<AddressSearchResult> } | { id: string; address_line1: string;  }} data
 * @returns {{ short_addresses: Array<AddressSearchResult> }}
 */

function mapData(data) {
  if ('short_addresses' in data) {
    return data;
  }

  return {
    short_addresses: [{
      id: data.id,
      potential_address: data.address_line1
    }]
  };
}
/**
 * @param {string} baseUrl
 * @param {string} postCode
 * @param {string} appId
 */


var getAddressDetails = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, postCode, appId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = [validateApiUrl(baseUrl)].filter(Boolean);

            if (!validations.length) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initNoBankIdApiConnector(baseUrl, appId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0
            }));
            path = buildUrl(addressSearch, {
              postCode: postCode.replace(/\s/g, '')
            });
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;

            if (!resp.ok) {
              _context.next = 14;
              break;
            }

            return _context.abrupt("return", mapData(resp.data));

          case 14:
            return _context.abrupt("return", parseError(resp));

          case 17:
            _context.prev = 17;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 21:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 17]]);
  }));

  return function getAddressDetails(_x, _x2, _x3) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * @param {string} baseUrl
 * @param {string} addressId
 * @param {string} appId
 */

var getAddressDetailsById = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, addressId, appId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = [validateApiUrl(baseUrl)].filter(Boolean);

            if (!validations.length) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initNoBankIdApiConnector(baseUrl, appId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0
            }));
            path = buildUrl(addressSearchById, {
              addressId: addressId
            });
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function getAddressDetailsById(_x, _x2, _x3) {
    return _ref.apply(this, arguments);
  };
}();

var index$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getAddressDetails: getAddressDetails,
  getAddressDetailsById: getAddressDetailsById
});

var getAllBeneficiariesPath="/banks/:bankId/accounts/:scope/beneficiaries?benType=:benType&search=:search&page=:page&size=:size";

/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} benType must be ALL/IFT/UK
 * @param {string} search
 * @param {string} page
 * @param {string} size
 */

var getAllBeneficiaries = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, benType, search, page, size) {
    var validations, apic, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            if (search === void 0) {
              search = '';
            }

            if (page === void 0) {
              page = '';
            }

            if (size === void 0) {
              size = '';
            }

            _context.prev = 3;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 8;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateBenType(benType));

            if (!validations.error) {
              _context.next = 8;
              break;
            }

            return _context.abrupt("return", validations);

          case 8:
            apic = initApiConnector(baseUrl, bankId);
            apic.setHeaders(_objectSpread(_objectSpread({}, apic.headers), {}, {
              Authorization: "Bearer " + token
            }));
            path = buildUrl(getAllBeneficiariesPath, {
              bankId: bankId,
              scope: 'default',
              benType: benType,
              search: search,
              page: page,
              size: size
            });
            _context.next = 13;
            return apic.get(path);

          case 13:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 17:
            _context.prev = 17;
            _context.t0 = _context["catch"](3);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 21:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[3, 17]]);
  }));

  return function getAllBeneficiaries(_x, _x2, _x3, _x4, _x5, _x6, _x7) {
    return _ref.apply(this, arguments);
  };
}();

var index$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getAllBeneficiaries: getAllBeneficiaries
});

var createDIPCasePath="/banks/:bankId/cases/dip";var updateDIPCasePath="/banks/:bankId/cases/dip/:caseId";var saveDIPCasePath="/banks/:bankId/cases/dip/:caseId/savecase";var retrieveDIPCasePath="/banks/:bankId/cases/dip/:uuid/retrievecase";var createCSAPCasePath="/banks/:bankId/cases/csap/savings/existing";var updateCSAPCasePath="/banks/:bankId/cases/csap/savings/existing/:caseId/tasks/capture/actions/update";var submitCSAPCasePath="/banks/:bankId/cases/csap/savings/existing/:caseId/tasks/capture/actions/submit";var getNextTaskPath="/banks/:bankId/cases/csap/existing/:caseId/tasks/next";var getOffersPath="/banks/:bankId/cases/csap/existing/:caseId/tasks/offers";var attachCaseEvidencePath="/banks/:bankId/cases/csap/existing/:caseId/evidence";var csapOfferDecisionPath="/banks/:bankId/cases/csap/existing/:caseId/tasks/offers/actions/decision";var getTaskCompletedPath="/banks/:bankId/cases/csap/existing/:caseId/tasks/completed";var cisaBalanceTransferPath="/banks/:bankId/cases/cisa/transfer";var attachCashISAEvidencePath="/banks/:bankId/cases/cisa/transfer/:caseId/evidence";var getLetterCodePath="/banks/:bankId/cases/csap/:customerType/:caseId/tasks/pending/lettercode";

/**
 * This will be used to attach evidence to the submitted CSAP case
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} userTrackingId
 * @param {string} caseId
 * @param {object} body
 */

var attachCaseEvidence = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, userTrackingId, caseId, body) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0,
              Authorization: "Bearer " + token
            }));
            path = buildUrl(attachCaseEvidencePath, {
              bankId: bankId,
              caseId: caseId
            });
            _context.next = 11;
            return apic.post(path, body);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function attachCaseEvidence(_x, _x2, _x3, _x4, _x5, _x6) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * This will be used to attach evidence to the submitted cash ISA transfer in request.
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} userTrackingId
 * @param {string} caseId
 * @param {object} body
 */

var attachCashISATransferEvidence = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, userTrackingId, caseId, body) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_1_0,
              Authorization: "Bearer " + token
            }));
            path = buildUrl(attachCashISAEvidencePath, {
              bankId: bankId,
              caseId: caseId
            });
            _context.next = 11;
            return apic.post(path, body);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function attachCashISATransferEvidence(_x, _x2, _x3, _x4, _x5, _x6) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * A balance transfer service used by cash-transfer-in webapp
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} userTrackingId
 * @param {object} body
 */

var cisaBalanceTransfer = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, userTrackingId, body) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_1_0,
              Authorization: "Bearer " + token
            }));
            path = buildUrl(cisaBalanceTransferPath, {
              bankId: bankId
            });
            _context.next = 11;
            return apic.post(path, body);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function cisaBalanceTransfer(_x, _x2, _x3, _x4, _x5) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * This will use to create CSAP case id
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} userTrackingId
 * @param {object} body
 */

var createCSAPCase = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, userTrackingId, body) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0,
              Authorization: "Bearer " + token
            }));
            path = buildUrl(createCSAPCasePath, {
              bankId: bankId
            });
            _context.next = 11;
            return apic.post(path, body);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function createCSAPCase(_x, _x2, _x3, _x4, _x5) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * This will use to create DIP case id
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} userTrackingId
 * @param {object} body
 */

var createDIPCase = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, userTrackingId, body) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateParamsExceptToken(baseUrl, bankId, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_2_0
            }));
            path = buildUrl(createDIPCasePath, {
              bankId: bankId
            });
            _context.next = 11;
            return apic.post(path, body);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function createDIPCase(_x, _x2, _x3, _x4) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * This service is used to capture customers decision on the presented offers
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} userTrackingId
 * @param {string} caseId
 * @param {object} body
 */

var csapOfferDecision = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, userTrackingId, caseId, body) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0,
              Authorization: "Bearer " + token
            }));
            path = buildUrl(csapOfferDecisionPath, {
              bankId: bankId,
              caseId: caseId
            });
            _context.next = 11;
            return apic.put(path, body);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function csapOfferDecision(_x, _x2, _x3, _x4, _x5, _x6) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * This will be used to Get Letter Code for continuation secondary applicant journey
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} userTrackingId
 * @param {string} caseId
 * @param {string} customerType
 */

var getLetterCode = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, userTrackingId, caseId, customerType) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0,
              Authorization: "Bearer " + token
            }));
            path = buildUrl(getLetterCodePath, {
              bankId: bankId,
              customerType: customerType,
              caseId: caseId
            });
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function getLetterCode(_x, _x2, _x3, _x4, _x5, _x6) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * This will be used to Get Next Task associated with CSAP case
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} userTrackingId
 * @param {string} caseId
 */

var getNextTask = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, userTrackingId, caseId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0,
              Authorization: "Bearer " + token
            }));
            path = buildUrl(getNextTaskPath, {
              bankId: bankId,
              caseId: caseId
            });
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function getNextTask(_x, _x2, _x3, _x4, _x5) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * This will be used to Get Offers associated with CSAP case
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} userTrackingId
 * @param {string} caseId
 */

var getOffers = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, userTrackingId, caseId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0,
              Authorization: "Bearer " + token
            }));
            path = buildUrl(getOffersPath, {
              bankId: bankId,
              caseId: caseId
            });
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function getOffers(_x, _x2, _x3, _x4, _x5) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * This will be used to Get Task - Completed associated with CSAP case
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} userTrackingId
 * @param {string} caseId
 */

var getTaskCompleted = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, userTrackingId, caseId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0,
              Authorization: "Bearer " + token
            }));
            path = buildUrl(getTaskCompletedPath, {
              bankId: bankId,
              caseId: caseId
            });
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function getTaskCompleted(_x, _x2, _x3, _x4, _x5) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * This will use to retrieve DIP case id
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} userTrackingId
 * @param {string} uuid
 */

var retrieveDIPCase = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, userTrackingId, uuid) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateParamsExceptToken(baseUrl, bankId, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_2_0
            }));
            path = buildUrl(retrieveDIPCasePath, {
              bankId: bankId,
              uuid: uuid
            });
            _context.next = 11;
            return apic.get(path, {
              uuid: uuid
            });

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function retrieveDIPCase(_x, _x2, _x3, _x4) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * This will use to update applicant details with DIP case id and return task id, eligible amount,eligible product
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} userTrackingId
 * @param {string} caseId
 * @param {object} body
 */

var saveDIPCase = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, userTrackingId, caseId, body) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateParamsExceptToken(baseUrl, bankId, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_2_0
            }));
            path = buildUrl(saveDIPCasePath, {
              bankId: bankId,
              caseId: caseId
            });
            _context.next = 11;
            return apic.post(path, body);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function saveDIPCase(_x, _x2, _x3, _x4, _x5) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * This will use to create CSAP case id
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} userTrackingId
 * @param {string} caseId
 * @param {object} body
 */

var submitCSAPCase = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, userTrackingId, caseId, body) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0,
              Authorization: "Bearer " + token
            }));
            path = buildUrl(submitCSAPCasePath, {
              bankId: bankId,
              caseId: caseId
            });
            _context.next = 11;
            return apic.put(path, body);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function submitCSAPCase(_x, _x2, _x3, _x4, _x5, _x6) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * This will use to update applicant details for a CSAP case
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} userTrackingId
 * @param {string} caseId
 * @param {object} body
 */

var updateCSAPCase = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, userTrackingId, caseId, body) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0,
              Authorization: "Bearer " + token
            }));
            path = buildUrl(updateCSAPCasePath, {
              bankId: bankId,
              caseId: caseId
            });
            _context.next = 11;
            return apic.put(path, body);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function updateCSAPCase(_x, _x2, _x3, _x4, _x5, _x6) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * This will use to update applicant details with DIP case id and return task id, eligible amount,eligible product
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} userTrackingId
 * @param {string} caseId
 * @param {object} body
 */

var updateDIPCase = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, userTrackingId, caseId, body) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateParamsExceptToken(baseUrl, bankId, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_2_0
            }));
            path = buildUrl(updateDIPCasePath, {
              bankId: bankId,
              caseId: caseId
            });
            _context.next = 11;
            return apic.put(path, body);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function updateDIPCase(_x, _x2, _x3, _x4, _x5) {
    return _ref.apply(this, arguments);
  };
}();

var index$3 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  attachCaseEvidence: attachCaseEvidence,
  attachCashISATransferEvidence: attachCashISATransferEvidence,
  cisaBalanceTransfer: cisaBalanceTransfer,
  createCSAPCase: createCSAPCase,
  createDIPCase: createDIPCase,
  csapOfferDecision: csapOfferDecision,
  getLetterCode: getLetterCode,
  getNextTask: getNextTask,
  getOffers: getOffers,
  getTaskCompleted: getTaskCompleted,
  retrieveDIPCase: retrieveDIPCase,
  saveDIPCase: saveDIPCase,
  submitCSAPCase: submitCSAPCase,
  updateCSAPCase: updateCSAPCase,
  updateDIPCase: updateDIPCase
});

var getCustomerByIdPath="/banks/:bankId/customers/:customerId";var getCustomerListPath="/banks/:bankId/customers";var updatePersonalCustomerPath="/banks/:bankId/customers/personal/:customerId";var updatePersonalCustomerPersonalDetailsPath="/banks/:bankId/customers/personal/:customerId/personaldetails?duplicate_check=default";var createPersonalCustomerPath="/banks/:bankId/customers/personal?duplicate_check=default";var createNTBPersonalCustomerPath="/banks/:bankId/customers/personal/prospect?duplicate_check=default";var updatePersonalCustomerContactDetails="/banks/:bankId/customers/personal/:customerId/contact";

/**
 * This will use to create personal customer
 *
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} userTrackingId
 * @param {object} body
 */

var createPersonalCustomer = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, userTrackingId, body, isNTBLoanJourney) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            if (isNTBLoanJourney === void 0) {
              isNTBLoanJourney = false;
            }

            _context.prev = 1;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 6;
              break;
            }

            validations = validateParamsExceptToken(baseUrl, bankId, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 6;
              break;
            }

            return _context.abrupt("return", validations);

          case 6:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0
            })); // @ts-ignore

            path = buildUrl(isNTBLoanJourney ? createNTBPersonalCustomerPath : createPersonalCustomerPath, {
              bankId: bankId
            });
            _context.next = 12;
            return apic.post(path, body);

          case 12:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 16:
            _context.prev = 16;
            _context.t0 = _context["catch"](1);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 20:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[1, 16]]);
  }));

  return function createPersonalCustomer(_x, _x2, _x3, _x4, _x5) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} userTrackingId
 * @param {string} customerId
 */

var getCustomerById = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, userTrackingId, customerId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token,
              'x-bpi-version': X_1_0_0
            }));
            path = buildUrl(getCustomerByIdPath, {
              bankId: bankId,
              customerId: customerId
            });
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function getCustomerById(_x, _x2, _x3, _x4, _x5) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} userTrackingId
 */
// todo why is this passing an object?

var getCustomerList = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, userTrackingId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token,
              'x-bpi-version': X_1_0_0
            }));
            path = buildUrl(getCustomerListPath, {
              bankId: bankId
            });
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function getCustomerList(_x, _x2, _x3, _x4) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * @typedef {object} Options
 * @property {string} baseUrl
 * @property {import('types').BankId} bankId
 * @property {string} token
 * @property {string} userTrackingId
 * @property {string} customerId
 * @property {any} [body]
 */

/**
 * This will use update customer details for those customer who have done ID & V check
 * @param {Options} options
 */

var updatePersonalCustomer = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(_ref) {
    var baseUrl, bankId, token, userTrackingId, customerId, body, validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            baseUrl = _ref.baseUrl, bankId = _ref.bankId, token = _ref.token, userTrackingId = _ref.userTrackingId, customerId = _ref.customerId, body = _ref.body;
            _context.prev = 1;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 6;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 6;
              break;
            }

            return _context.abrupt("return", validations);

          case 6:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token,
              'x-bpi-version': X_1_0_0
            }));
            path = buildUrl(updatePersonalCustomerPath, {
              bankId: bankId,
              customerId: customerId
            });
            _context.next = 12;
            return apic.put(path, body);

          case 12:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 16:
            _context.prev = 16;
            _context.t0 = _context["catch"](1);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 20:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[1, 16]]);
  }));

  return function updatePersonalCustomer(_x) {
    return _ref2.apply(this, arguments);
  };
}();

/**
 * @typedef {object} Options
 * @property {string} baseUrl
 * @property {import('types').BankId} bankId
 * @property {string} token
 * @property {string} userTrackingId
 * @property {string} customerId
 * @property {any} [body]
 */

/**
 * This will use update customer details for those customer who have not done ID & V check
 * @param {Options} options
 */

var updatePersonalCustomerPersonalDetails = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(_ref) {
    var baseUrl, bankId, token, userTrackingId, customerId, body, validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            baseUrl = _ref.baseUrl, bankId = _ref.bankId, token = _ref.token, userTrackingId = _ref.userTrackingId, customerId = _ref.customerId, body = _ref.body;
            _context.prev = 1;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 6;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 6;
              break;
            }

            return _context.abrupt("return", validations);

          case 6:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token,
              'x-bpi-version': X_1_0_0
            }));
            path = buildUrl(updatePersonalCustomerPersonalDetailsPath, {
              bankId: bankId,
              customerId: customerId
            });
            _context.next = 12;
            return apic.put(path, body);

          case 12:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 16:
            _context.prev = 16;
            _context.t0 = _context["catch"](1);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 20:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[1, 16]]);
  }));

  return function updatePersonalCustomerPersonalDetails(_x) {
    return _ref2.apply(this, arguments);
  };
}();

/**
 * @typedef {object} Options
 * @property {string} baseUrl
 * @property {import('types').BankId} bankId
 * @property {string} token
 * @property {string} userTrackingId
 * @property {string} customerId
 * @property {any} [body]
 */

/**
 * This will use update customer details for those customer who have not done ID & V check
 * @param {Options} options
 */

var updatePersonalCustomerContactDetails$1 = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(_ref) {
    var baseUrl, bankId, token, userTrackingId, customerId, body, validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            baseUrl = _ref.baseUrl, bankId = _ref.bankId, token = _ref.token, userTrackingId = _ref.userTrackingId, customerId = _ref.customerId, body = _ref.body;
            _context.prev = 1;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 6;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 6;
              break;
            }

            return _context.abrupt("return", validations);

          case 6:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token,
              'x-bpi-version': X_1_0_0
            }));
            path = buildUrl(updatePersonalCustomerContactDetails, {
              bankId: bankId,
              customerId: customerId
            });
            _context.next = 12;
            return apic.put(path, body);

          case 12:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 16:
            _context.prev = 16;
            _context.t0 = _context["catch"](1);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 20:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[1, 16]]);
  }));

  return function updatePersonalCustomerContactDetails(_x) {
    return _ref2.apply(this, arguments);
  };
}();

var index$4 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  createPersonalCustomer: createPersonalCustomer,
  getCustomerById: getCustomerById,
  getCustomerList: getCustomerList,
  updatePersonalCustomer: updatePersonalCustomer,
  updatePersonalCustomerPersonalDetails: updatePersonalCustomerPersonalDetails,
  updatePersonalCustomerContactDetails: updatePersonalCustomerContactDetails$1
});

var getUserInsightsEndPoint="/banks/:bankId/insights/settings/user";

/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 */

var getUserInsights = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateParamsExceptToken(baseUrl, bankId);

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0,
              Authorization: "Bearer " + token
            }));
            path = buildUrl(getUserInsightsEndPoint, {
              bankId: bankId
            });
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function getUserInsights(_x, _x2, _x3) {
    return _ref.apply(this, arguments);
  };
}();

var index$5 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getUserInsights: getUserInsights
});

var eligibilityRefDataEndPoint="/banks/:bankId/ref/content/productinfo/current?is_overdraft=true";

/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 */

var getEligibleRefData = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateParamsExceptToken(baseUrl, bankId);

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0
            }));
            path = buildUrl(eligibilityRefDataEndPoint, {
              bankId: bankId
            });
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function getEligibleRefData(_x, _x2) {
    return _ref.apply(this, arguments);
  };
}();

var index$6 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getEligibleRefData: getEligibleRefData
});

var secureMessagingEndPoint="/banks/:bankId/securemessages";

/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 */

var getSecureMessages = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateParamsExceptToken(baseUrl, bankId);

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0,
              Authorization: "Bearer " + token
            }));
            path = buildUrl(secureMessagingEndPoint, {
              bankId: bankId
            });
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function getSecureMessages(_x, _x2, _x3) {
    return _ref.apply(this, arguments);
  };
}();

var index$7 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getSecureMessages: getSecureMessages
});

var tokenChallengeEndpoint="/banks/:bankId/auth/provider/oauth2/token/challenges";var accessTokenEndpoint="/banks/:bankId/auth/provider/oauth2/token";var getPublicKeyEndpoint="/banks/:bankId/auth/provider/rsa/public_key";

/**
 * todo add the link to the endpoint docs here
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} userId
 * @param {any} scope
 */

var createTokenChallenge = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, userId, scope) {
    var validations, apic, payload, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateParamsExceptToken(baseUrl, bankId);

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            payload = {
              user_identity: {
                user_id: userId
              },
              scope: scope
            };
            path = buildUrl(tokenChallengeEndpoint, {
              bankId: bankId
            });
            _context.next = 10;
            return apic.post(path, payload);

          case 10:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 14:
            _context.prev = 14;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 18:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 14]]);
  }));

  return function createTokenChallenge(_x, _x2, _x3, _x4) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {any} grantType
 * @param {string | number} scope
 * @param {string} token
 */

var createAccessToken = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, grantType, scope, token) {
    var validations, apic, payload, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token);

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            apic.setHeaders(_objectSpread(_objectSpread({}, apic.headers), {}, {
              'content-type': 'application/x-www-form-urlencoded',
              Authorization: "Basic " + token
            }));
            payload = "grant_type=" + grantType + "&scope=" + scope;
            path = buildUrl(accessTokenEndpoint, {
              bankId: bankId
            });
            _context.next = 11;
            return apic.post(path, payload);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function createAccessToken(_x, _x2, _x3, _x4, _x5) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 */

var getPublicKey = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateParamsExceptToken(baseUrl, bankId);

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0
            }));
            path = buildUrl(getPublicKeyEndpoint, {
              bankId: bankId
            });
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function getPublicKey(_x, _x2) {
    return _ref.apply(this, arguments);
  };
}();

var index$8 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  createTokenChallenge: createTokenChallenge,
  createAccessToken: createAccessToken,
  getPublicKey: getPublicKey
});

var userPreferencesEndpoint="/user/preferences";var userTokenEndpoint="/user/token";var userDetailsEndpoint="/user/details";var getCurrentUserEndpoint="/user";var getCredentialsEndpoint="/banks/:bankId/user/credentials";

/**
 * Gets the user preferences
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 */

function getUserPreferences(_x, _x2, _x3) {
  return _getUserPreferences.apply(this, arguments);
}

function _getUserPreferences() {
  _getUserPreferences = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token) {
    var validations, apic, headers, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token);

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            _context.next = 10;
            return apic.get(userPreferencesEndpoint);

          case 10:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 14:
            _context.prev = 14;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 18:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 14]]);
  }));
  return _getUserPreferences.apply(this, arguments);
}

var yesNoValues = ['Y', 'N'];
/**
 * @param {import("src/services/user/methods").UserPreferences} userPreferences
 */

var validateUserPreferences = function validateUserPreferences(userPreferences) {
  var userPreferencesSchema = object().shape({
    marketing_preferences: array().of(object({
      type: string(),
      value: string().oneOf(yesNoValues)
    })).notRequired(),
    communication_preferences: array().of(object({
      type: string(),
      value: string().oneOf(yesNoValues),
      accounts: array().of(object({
        id: string().test(isGuid)
      })).notRequired()
    })).notRequired()
  }).noUnknown().strict(true); // @ts-ignore
  // Some oddness with this one as `notRequired` should allow this to be set as nullable.

  return validate(userPreferencesSchema, userPreferences);
};

/**
 * Gets the user preferences
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {import('..').UserPreferences} preferences
 */

function setUserPreferences(_x, _x2, _x3, _x4) {
  return _setUserPreferences.apply(this, arguments);
}

function _setUserPreferences() {
  _setUserPreferences = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, preferences) {
    var validations, apic, headers, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserPreferences(preferences));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            _context.next = 10;
            return apic.put(userPreferencesEndpoint, preferences);

          case 10:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 14:
            _context.prev = 14;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 18:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 14]]);
  }));
  return _setUserPreferences.apply(this, arguments);
}

/**
 * Gets the user preferences
 * @param {string} baseUrl
 * @param {string} token
 * @param {string} userTrackingId
 * @param {string} appId
 */

function getUserBankIdByToken(_x, _x2, _x3, _x4) {
  return _getUserBankIdByToken.apply(this, arguments);
}

function _getUserBankIdByToken() {
  _getUserBankIdByToken = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, token, userTrackingId, appId) {
    var validations, apic, headers, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = [validateApiUrl(baseUrl), validateAccessToken(token), validateUserTrackingId(userTrackingId)].filter(Boolean);

            if (!validations.length) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initNoBankIdApiConnector(baseUrl, appId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            _context.next = 10;
            return apic.get(userTokenEndpoint);

          case 10:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 14:
            _context.prev = 14;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 18:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 14]]);
  }));
  return _getUserBankIdByToken.apply(this, arguments);
}

/**
 * Gets the user details
 * @param {string} baseUrl
 * @param {string} token
 * @param {string} appId
 */

function getUserDetails(_x, _x2, _x3) {
  return _getUserDetails.apply(this, arguments);
}

function _getUserDetails() {
  _getUserDetails = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, token, appId) {
    var validations, apic, headers, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = [validateApiUrl(baseUrl), validateAccessToken(token)].filter(Boolean);

            if (!validations.length) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initNoBankIdApiConnector(baseUrl, appId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            _context.next = 10;
            return apic.get(userDetailsEndpoint);

          case 10:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 14:
            _context.prev = 14;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 18:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 14]]);
  }));
  return _getUserDetails.apply(this, arguments);
}

/**
 * Gets the current user details viz username and contact details
 * @param {string} baseUrl
 * @param {string} token
 * @param {string} userTrackingId
 * @param {string} appId
 */

function getCurrentUser(_x, _x2, _x3, _x4) {
  return _getCurrentUser.apply(this, arguments);
}

function _getCurrentUser() {
  _getCurrentUser = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, token, userTrackingId, appId) {
    var validations, apic, headers, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = [validateApiUrl(baseUrl), validateAccessToken(token), validateUserTrackingId(userTrackingId)].filter(Boolean);

            if (!validations.length) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initNoBankIdApiConnector(baseUrl, appId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_0_8_0,
              Authorization: "Bearer " + token
            }));
            _context.next = 10;
            return apic.get(getCurrentUserEndpoint);

          case 10:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 14:
            _context.prev = 14;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 18:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 14]]);
  }));
  return _getCurrentUser.apply(this, arguments);
}

/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} userTrackingId
 */

var getCredentials = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, userTrackingId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_0_8_0,
              Authorization: "Bearer " + token
            }));
            path = buildUrl(getCredentialsEndpoint, {
              bankId: bankId
            });
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function getCredentials(_x, _x2, _x3, _x4) {
    return _ref.apply(this, arguments);
  };
}();

var index$9 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getUserPreferences: getUserPreferences,
  setUserPreferences: setUserPreferences,
  getUserBankIdByToken: getUserBankIdByToken,
  getUserDetails: getUserDetails,
  getCurrentUser: getCurrentUser,
  getCredentials: getCredentials
});

var getAllVerificationsEndPoint="/banks/:bankId/user/:scope/verification/all";

/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 */

var getAllVerifications = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token);

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0,
              Authorization: "Bearer " + token
            }));
            path = buildUrl(getAllVerificationsEndPoint, {
              bankId: bankId,
              scope: 'default'
            });
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function getAllVerifications(_x, _x2, _x3) {
    return _ref.apply(this, arguments);
  };
}();

var index$a = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getAllVerifications: getAllVerifications
});

var searchRecordsRMPath="/record/search";var getApplicationRMPath="/application/:decisionReference";var addCommentRMPath="/application/:decisionReference/comment";

/**
 * Gets the user details
 * @param {string} baseUrl
 * @param {object} body
 * @param {string} decisionReference
 * @param {string} token
 * @param {import('types').BankId} bankId
 * @param {string} customerNumber
 * @param {number} apiTrustLevel
 */

function addComment(_x, _x2, _x3, _x4, _x5, _x6, _x7) {
  return _addComment.apply(this, arguments);
}

function _addComment() {
  _addComment = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, body, decisionReference, token, bankId, customerNumber, apiTrustLevel) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateDecisionReference(decisionReference));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnectorStaffOnly(baseUrl, bankId, customerNumber, apiTrustLevel);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            path = buildUrl(addCommentRMPath, {
              decisionReference: decisionReference
            });
            _context.next = 11;
            return apic.post(path, body);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));
  return _addComment.apply(this, arguments);
}

/**
 * Gets the user details
 * @param {string} baseUrl
 * @param {string} decisionReference
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} customerNumber
 * @param {number} apiTrustLevel
 * */

function getApplicationByDecisionReference(_x, _x2, _x3, _x4, _x5, _x6) {
  return _getApplicationByDecisionReference.apply(this, arguments);
}

function _getApplicationByDecisionReference() {
  _getApplicationByDecisionReference = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, decisionReference, bankId, token, customerNumber, apiTrustLevel) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateDecisionReference(decisionReference));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnectorStaffOnly(baseUrl, bankId, customerNumber, apiTrustLevel);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            path = buildUrl(getApplicationRMPath, {
              decisionReference: decisionReference
            });
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));
  return _getApplicationByDecisionReference.apply(this, arguments);
}

/**
 * Gets the user details
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {object} body
 * @param {string} token
 * @param {string} customerNumber
 * @param {number} apiTrustLevel
 */

function searchRecords(_x, _x2, _x3, _x4, _x5, _x6) {
  return _searchRecords.apply(this, arguments);
}

function _searchRecords() {
  _searchRecords = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, body, bankId, token, customerNumber, apiTrustLevel) {
    var validations, apic, headers, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token);

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnectorStaffOnly(baseUrl, bankId, customerNumber, apiTrustLevel);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            _context.next = 10;
            return apic.put(searchRecordsRMPath, body);

          case 10:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 14:
            _context.prev = 14;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 18:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 14]]);
  }));
  return _searchRecords.apply(this, arguments);
}

var index$b = /*#__PURE__*/Object.freeze({
  __proto__: null,
  addComment: addComment,
  getApplicationByDecisionReference: getApplicationByDecisionReference,
  searchRecords: searchRecords
});

var requestCustomerNumberBySCAN="/banks/customers/customernumber/search";

/**
 * Looks up customer number to send an SMS notification
 * @param {string} baseUrl
 * @param {object} body
 * @param {string} appId
 */

function requestCustomerNumberBySCAN$1(_x, _x2, _x3) {
  return _requestCustomerNumberBySCAN.apply(this, arguments);
}

function _requestCustomerNumberBySCAN() {
  _requestCustomerNumberBySCAN = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, body, appId) {
    var validations, apic, headers, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateNoBrandCommonMethodParams(baseUrl);

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initNoBankIdApiConnector(baseUrl, appId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread({}, headers));
            _context.next = 10;
            return apic.post(requestCustomerNumberBySCAN, body);

          case 10:
            resp = _context.sent;

            if (!resp.ok) {
              _context.next = 13;
              break;
            }

            return _context.abrupt("return", resp.ok);

          case 13:
            return _context.abrupt("return", parseError(resp));

          case 16:
            _context.prev = 16;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 20:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 16]]);
  }));
  return _requestCustomerNumberBySCAN.apply(this, arguments);
}

var index$c = /*#__PURE__*/Object.freeze({
  __proto__: null,
  requestCustomerNumberBySCAN: requestCustomerNumberBySCAN$1
});

var createOAuthJWTTokenPath="/banks/:bankId/auth/provider/oauth2/token/jwt";var exchangeOAuthJWTTokenPath="/banks/:bankId/auth/provider/oauth2/token/exchange/jwt";var shortTermAccessTokenPath="/banks/:bankId/auth/provider/oauth2/token/shortterm";var getPublicKeyPath="/banks/auth/provider/oauth2/jwt/publickey";

/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} body
 * @param {string} encodedChallengeRequest
 * @param {string} userTrackingId
 * @param {string} appTitle
 */

var createOAuthJWTToken = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, body, encodedChallengeRequest, userTrackingId, appTitle) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateParamsExceptToken(baseUrl, bankId, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId, appTitle);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'content-type': 'application/x-www-form-urlencoded',
              Authorization: encodedChallengeRequest
            }));
            path = buildUrl(createOAuthJWTTokenPath, {
              bankId: bankId
            });
            _context.next = 11;
            return apic.post(path, body);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function createOAuthJWTToken(_x, _x2, _x3, _x4, _x5, _x6) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} body
 * @param {string} shortAccessToken
 * @param {string} userTrackingId
 */

var exchangeOAuthJWTToken = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, body, shortAccessToken, userTrackingId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, shortAccessToken, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'content-type': 'application/x-www-form-urlencoded',
              Authorization: "Bearer " + shortAccessToken
            }));
            path = buildUrl(exchangeOAuthJWTTokenPath, {
              bankId: bankId
            });
            _context.next = 11;
            return apic.post(path, body);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function exchangeOAuthJWTToken(_x, _x2, _x3, _x4, _x5) {
    return _ref.apply(this, arguments);
  };
}();

/**
 *  @param {string} baseUrl
 *  @param {import('types').BankId} bankId
 *  @param {string} userTrackingId
 *  @param {string} appTitle
 */

var getPublicKey$1 = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, userTrackingId, appTitle) {
    var validations, apic, headers, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = [validateApiUrl(baseUrl), validateUserTrackingId(userTrackingId)].filter(Boolean);

            if (!validations.length) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId, appTitle);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread({}, headers));
            _context.next = 10;
            return apic.get(getPublicKeyPath);

          case 10:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 14:
            _context.prev = 14;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 18:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 14]]);
  }));

  return function getPublicKey(_x, _x2, _x3, _x4) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} body
 * @param {string} accessToken
 * @param {string} userTrackingId
 */

var shortTermAccessToken = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, body, accessToken, userTrackingId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, accessToken, validateUserTrackingId(userTrackingId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId, userTrackingId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'content-type': 'application/x-www-form-urlencoded',
              Authorization: "Bearer " + accessToken
            }));
            path = buildUrl(shortTermAccessTokenPath, {
              bankId: bankId
            });
            _context.next = 11;
            return apic.post(path, body);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function shortTermAccessToken(_x, _x2, _x3, _x4, _x5) {
    return _ref.apply(this, arguments);
  };
}();

var index$d = /*#__PURE__*/Object.freeze({
  __proto__: null,
  createOAuthJWTToken: createOAuthJWTToken,
  exchangeOAuthJWTToken: exchangeOAuthJWTToken,
  getPublicKey: getPublicKey$1,
  shortTermAccessToken: shortTermAccessToken
});

var getSecurityDevicesEndPoint="/banks/:bankId/user/security_devices";

/**
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 */

var getSecurityDevices = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateParamsExceptToken(baseUrl, bankId);

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_0_0,
              Authorization: "Bearer " + token
            }));
            path = buildUrl(getSecurityDevicesEndPoint, {
              bankId: bankId
            });
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));

  return function getSecurityDevices(_x, _x2, _x3) {
    return _ref.apply(this, arguments);
  };
}();

var index$e = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getSecurityDevices: getSecurityDevices
});

var confirmContactDetailsEndPoint="/user/confirmation";

/**
 * @typedef {object} Options
 * @property {string} baseUrl
 * @property {import('types').BankId} bankId
 * @property {string} token
 * @property {any} body
 */

/**
 * Requests confirmation of the specified contact detail.
 * @param {Options} options
 */

var confirmContactDetails = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(_ref) {
    var baseUrl, bankId, token, body, validations, apic, headers, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            baseUrl = _ref.baseUrl, bankId = _ref.bankId, token = _ref.token, body = _ref.body;
            _context.prev = 1;
            validations = [validateApiUrl(baseUrl), validateAccessToken(token)].filter(Boolean);

            if (!validations.length) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              'x-bpi-version': X_1_2_0,
              Authorization: "Bearer " + token
            }));
            _context.next = 10;
            return apic.post(confirmContactDetailsEndPoint, body);

          case 10:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 14:
            _context.prev = 14;
            _context.t0 = _context["catch"](1);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 18:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[1, 14]]);
  }));

  return function confirmContactDetails(_x) {
    return _ref2.apply(this, arguments);
  };
}();

var index$f = /*#__PURE__*/Object.freeze({
  __proto__: null,
  confirmContactDetails: confirmContactDetails
});

var createWalletEndpoint="/wallet";var getWalletEndpoint="/wallet/:walletId";var getWalletSummaryEndpoint="/wallet/:walletId/summary";var getWalletCustomerEndpoint="/wallet/:walletId/customer";var getWalletContactEndpoint="/wallet/:walletId/contact";var getWalletTermsAndConditionsEndpoint="/wallet/:walletId/termsAndConditions";var getWalletTermsAndConditionsLatestEndpoint="/wallet/:walletId/termsAndConditions/latestAvailable";var getWalletLinkedProductsEndpoint="/wallet/:walletId/linkedProducts";var updateWalletCustomerEndpoint="/wallet/:walletId/customer";var updateWalletContactEndpoint="/wallet/:walletId/contact";var updateWalletEndpoint="/wallet/:walletId";var updateWalletTermsAndConditionsEndpoint="/wallet/:walletId/termsAndConditions";var updateWalletLinkedProductsEndpoint="/wallet/:walletId/linkedProducts";var endpoints = {createWalletEndpoint:createWalletEndpoint,getWalletEndpoint:getWalletEndpoint,getWalletSummaryEndpoint:getWalletSummaryEndpoint,getWalletCustomerEndpoint:getWalletCustomerEndpoint,getWalletContactEndpoint:getWalletContactEndpoint,getWalletTermsAndConditionsEndpoint:getWalletTermsAndConditionsEndpoint,getWalletTermsAndConditionsLatestEndpoint:getWalletTermsAndConditionsLatestEndpoint,getWalletLinkedProductsEndpoint:getWalletLinkedProductsEndpoint,updateWalletCustomerEndpoint:updateWalletCustomerEndpoint,updateWalletContactEndpoint:updateWalletContactEndpoint,updateWalletEndpoint:updateWalletEndpoint,updateWalletTermsAndConditionsEndpoint:updateWalletTermsAndConditionsEndpoint,updateWalletLinkedProductsEndpoint:updateWalletLinkedProductsEndpoint};

var endpoints$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  createWalletEndpoint: createWalletEndpoint,
  getWalletEndpoint: getWalletEndpoint,
  getWalletSummaryEndpoint: getWalletSummaryEndpoint,
  getWalletCustomerEndpoint: getWalletCustomerEndpoint,
  getWalletContactEndpoint: getWalletContactEndpoint,
  getWalletTermsAndConditionsEndpoint: getWalletTermsAndConditionsEndpoint,
  getWalletTermsAndConditionsLatestEndpoint: getWalletTermsAndConditionsLatestEndpoint,
  getWalletLinkedProductsEndpoint: getWalletLinkedProductsEndpoint,
  updateWalletCustomerEndpoint: updateWalletCustomerEndpoint,
  updateWalletContactEndpoint: updateWalletContactEndpoint,
  updateWalletEndpoint: updateWalletEndpoint,
  updateWalletTermsAndConditionsEndpoint: updateWalletTermsAndConditionsEndpoint,
  updateWalletLinkedProductsEndpoint: updateWalletLinkedProductsEndpoint,
  'default': endpoints
});

/**
 * Gets new Wallet Details
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {import('..').CreateWalletReqStub} createWalletReqStub
 */

function createWallet(_x, _x2, _x3, _x4) {
  return _createWallet.apply(this, arguments);
}

function _createWallet() {
  _createWallet = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, createWalletReqStub) {
    var validations, apic, headers, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateCreateWalletDetails(createWalletReqStub));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            _context.next = 10;
            return apic.post(createWalletEndpoint, createWalletReqStub);

          case 10:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 14:
            _context.prev = 14;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 18:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 14]]);
  }));
  return _createWallet.apply(this, arguments);
}

/**
 * Gets new Wallet Details
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} walletId
 */

function getWallet(_x, _x2, _x3, _x4) {
  return _getWallet.apply(this, arguments);
}

function _getWallet() {
  _getWallet = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, walletId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateWalletId(walletId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            path = buildUrl(getWalletEndpoint, {
              walletId: walletId
            });
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));
  return _getWallet.apply(this, arguments);
}

/**
 * Gets new Wallet Details
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} walletId
 */

function getWalletSummary(_x, _x2, _x3, _x4) {
  return _getWalletSummary.apply(this, arguments);
}

function _getWalletSummary() {
  _getWalletSummary = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, walletId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateWalletId(walletId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            path = buildUrl(getWalletSummaryEndpoint, {
              walletId: walletId
            });
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));
  return _getWalletSummary.apply(this, arguments);
}

/**
 * Gets new Wallet Details
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} walletId
 */

function getWalletCustomer(_x, _x2, _x3, _x4) {
  return _getWalletCustomer.apply(this, arguments);
}

function _getWalletCustomer() {
  _getWalletCustomer = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, walletId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateWalletId(walletId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            path = buildUrl(getWalletCustomerEndpoint, {
              walletId: walletId
            });
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));
  return _getWalletCustomer.apply(this, arguments);
}

/**
 * Gets new Wallet Details
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} walletId
 */

function getWalletContact(_x, _x2, _x3, _x4) {
  return _getWalletContact.apply(this, arguments);
}

function _getWalletContact() {
  _getWalletContact = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, walletId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateWalletId(walletId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            path = buildUrl(getWalletContactEndpoint, {
              walletId: walletId
            });
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));
  return _getWalletContact.apply(this, arguments);
}

/**
 * Gets new Wallet Details
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} walletId
 */

function getWalletTermsAndConditions(_x, _x2, _x3, _x4) {
  return _getWalletTermsAndConditions.apply(this, arguments);
}

function _getWalletTermsAndConditions() {
  _getWalletTermsAndConditions = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, walletId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateWalletId(walletId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            path = buildUrl(getWalletTermsAndConditionsEndpoint, {
              walletId: walletId
            });
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));
  return _getWalletTermsAndConditions.apply(this, arguments);
}

/**
 * Gets new Wallet Details
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} walletId
 */

function getWalletTermsAndConditionsLatest(_x, _x2, _x3, _x4) {
  return _getWalletTermsAndConditionsLatest.apply(this, arguments);
}

function _getWalletTermsAndConditionsLatest() {
  _getWalletTermsAndConditionsLatest = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, walletId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateWalletId(walletId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            path = buildUrl(getWalletTermsAndConditionsLatestEndpoint, {
              walletId: walletId
            });
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));
  return _getWalletTermsAndConditionsLatest.apply(this, arguments);
}

/**
 * Gets new Wallet Details
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} walletId
 */

function getWalletLinkedProducts(_x, _x2, _x3, _x4) {
  return _getWalletLinkedProducts.apply(this, arguments);
}

function _getWalletLinkedProducts() {
  _getWalletLinkedProducts = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, walletId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateWalletId(walletId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            path = buildUrl(getWalletLinkedProductsEndpoint, {
              walletId: walletId
            });
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));
  return _getWalletLinkedProducts.apply(this, arguments);
}

/**
 * Gets new Wallet Details
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {import('types').UpdateWalletReqStub} updateWalletReqStub
 * @param {string} walletId
 */

function updateWallet(_x, _x2, _x3, _x4, _x5) {
  return _updateWallet.apply(this, arguments);
}

function _updateWallet() {
  _updateWallet = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, updateWalletReqStub, walletId) {
    var reqStubValidation, walletIdValidation, validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 7;
              break;
            }

            reqStubValidation = validateUpdateWalletReqStub(updateWalletReqStub);
            walletIdValidation = validateWalletId(walletId);
            validations = validateCommonMethodParams(baseUrl, bankId, token, walletIdValidation, reqStubValidation);

            if (!validations.error) {
              _context.next = 7;
              break;
            }

            return _context.abrupt("return", validations);

          case 7:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            path = buildUrl(updateWalletEndpoint, {
              walletId: walletId
            });
            _context.next = 13;
            return apic.put(path, updateWalletReqStub);

          case 13:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 17:
            _context.prev = 17;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 21:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 17]]);
  }));
  return _updateWallet.apply(this, arguments);
}

/**
 * Gets new Wallet Details
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {import('types').UpdateWalletCustomerStub} updateWalletCustomerReqStub
 * @param {string} walletId
 */

function updateWalletCustomer(_x, _x2, _x3, _x4, _x5) {
  return _updateWalletCustomer.apply(this, arguments);
}

function _updateWalletCustomer() {
  _updateWalletCustomer = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, updateWalletCustomerReqStub, walletId) {
    var reqStubValidation, walletIdValidation, validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 7;
              break;
            }

            reqStubValidation = validateUpdateWalletCustomerStub(updateWalletCustomerReqStub);
            walletIdValidation = validateWalletId(walletId);
            validations = validateCommonMethodParams(baseUrl, bankId, token, walletIdValidation, reqStubValidation);

            if (!validations.error) {
              _context.next = 7;
              break;
            }

            return _context.abrupt("return", validations);

          case 7:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            path = buildUrl(updateWalletCustomerEndpoint, {
              walletId: walletId
            });
            _context.next = 13;
            return apic.put(path, updateWalletCustomerReqStub);

          case 13:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 17:
            _context.prev = 17;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 21:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 17]]);
  }));
  return _updateWalletCustomer.apply(this, arguments);
}

/**
 * Gets new Wallet Details
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {import('types').UpdateWalletContactStub} updateWalletContactReqStub
 * @param {string} walletId
 */

function updateWalletContact(_x, _x2, _x3, _x4, _x5) {
  return _updateWalletContact.apply(this, arguments);
}

function _updateWalletContact() {
  _updateWalletContact = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, updateWalletContactReqStub, walletId) {
    var reqStubValidation, walletIdValidation, validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 7;
              break;
            }

            reqStubValidation = validateUpdateWalletContactStub(updateWalletContactReqStub);
            walletIdValidation = validateWalletId(walletId);
            validations = validateCommonMethodParams(baseUrl, bankId, token, walletIdValidation, reqStubValidation);

            if (!validations.error) {
              _context.next = 7;
              break;
            }

            return _context.abrupt("return", validations);

          case 7:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            path = buildUrl(updateWalletContactEndpoint, {
              walletId: walletId
            });
            _context.next = 13;
            return apic.put(path, updateWalletContactReqStub);

          case 13:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 17:
            _context.prev = 17;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 21:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 17]]);
  }));
  return _updateWalletContact.apply(this, arguments);
}

/**
 * Gets new Wallet Details
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {import('types').UpdateWalletTermsAndConditionsStub} updateWalletTermsAndConditionsReqStub
 * @param {string} walletId
 */

function updateWalletTermsAndConditions(_x, _x2, _x3, _x4, _x5) {
  return _updateWalletTermsAndConditions.apply(this, arguments);
}

function _updateWalletTermsAndConditions() {
  _updateWalletTermsAndConditions = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, updateWalletTermsAndConditionsReqStub, walletId) {
    var reqStubValidation, walletIdValidation, validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 7;
              break;
            }

            reqStubValidation = validateUpdateWalletTermsAndConditionsStub(updateWalletTermsAndConditionsReqStub);
            walletIdValidation = validateWalletId(walletId);
            validations = validateCommonMethodParams(baseUrl, bankId, token, walletIdValidation, reqStubValidation);

            if (!validations.error) {
              _context.next = 7;
              break;
            }

            return _context.abrupt("return", validations);

          case 7:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            path = buildUrl(updateWalletTermsAndConditionsEndpoint, {
              walletId: walletId
            });
            _context.next = 13;
            return apic.put(path, updateWalletTermsAndConditionsReqStub);

          case 13:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 17:
            _context.prev = 17;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 21:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 17]]);
  }));
  return _updateWalletTermsAndConditions.apply(this, arguments);
}

/**
 * Gets new Wallet Details
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {import('types').UpdateWalletLinkedProductsStub} updateWalletLinkedProductsReqStub
 * @param {string} walletId
 */

function updateWalletLinkedProducts(_x, _x2, _x3, _x4, _x5) {
  return _updateWalletLinkedProducts.apply(this, arguments);
}

function _updateWalletLinkedProducts() {
  _updateWalletLinkedProducts = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, updateWalletLinkedProductsReqStub, walletId) {
    var reqStubValidation, walletIdValidation, validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 7;
              break;
            }

            reqStubValidation = validateUpdateWalletLinkedProductsStub(updateWalletLinkedProductsReqStub);
            walletIdValidation = validateWalletId(walletId);
            validations = validateCommonMethodParams(baseUrl, bankId, token, walletIdValidation, reqStubValidation);

            if (!validations.error) {
              _context.next = 7;
              break;
            }

            return _context.abrupt("return", validations);

          case 7:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            path = buildUrl(updateWalletLinkedProductsEndpoint, {
              walletId: walletId
            });
            _context.next = 13;
            return apic.put(path, updateWalletLinkedProductsReqStub);

          case 13:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 17:
            _context.prev = 17;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 21:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 17]]);
  }));
  return _updateWalletLinkedProducts.apply(this, arguments);
}

function createCommonjsModule(fn, basedir, module) {
	return module = {
	  path: basedir,
	  exports: {},
	  require: function (path, base) {
      return commonjsRequire(path, (base === undefined || base === null) ? module.path : base);
    }
	}, fn(module, module.exports), module.exports;
}

function getCjsExportFromNamespace (n) {
	return n && n['default'] || n;
}

function commonjsRequire () {
	throw new Error('Dynamic requires are not currently supported by @rollup/plugin-commonjs');
}

var require$$0 = getCjsExportFromNamespace(endpoints$1);

var createWallet_stub = createCommonjsModule(function (module) {
  var createWalletEndpoint = require$$0.createWalletEndpoint;
  /**
   *
   * @param {any} req
   * @param {any} res
   */

  var middleware = function middleware(req, res) {
    res.json(module.exports.stubData.createWalletResStub);
  };

  module.exports.stubConfig = {
    route: createWalletEndpoint,
    method: 'post',
    middleware: middleware
  };
  module.exports.stubData = {
    createWalletReqStub: {
      customer: {
        firstName: 'First',
        lastName: 'Last',
        dateOfBirth: '2000-09-22',
        aliasName: 'Other'
      },
      contact: {
        email: 'noreply@cybg.com',
        mobileCountryCode: '44',
        mobileNumber: '07711000000'
      },
      termsAndConditions: {
        termsAndConditionsId: '3520aa00-101a-4940-be15-3c963b6109a3',
        termsAndConditionsVersion: '1.0.0',
        acceptanceDateTime: '2022-07-01T17:00:00.000+01:00'
      },
      linkedProducts: {
        vRedId: '7770aa00-101a-4940-be15-3c963b6109b3',
        vAtlanticId: '6660aa00-101a-4940-be15-3c963b6109b3'
      }
    },
    createWalletResStub: {
      walletId: '9900aa00-101a-4940-be15-3c963b6109b3',
      walletType: 'FULL',
      walletStatus: 'ACTIVE',
      createdDateTime: '2022-07-01T17:00:00.000+01:00',
      activationDateTime: '2022-07-01T17:00:00.000+01:00',
      lastStatusChangeDateTime: '2022-07-01T17:00:00.000+01:00',
      customer: {
        firstName: 'First',
        lastName: 'Last',
        dateOfBirth: '2000-09-22',
        aliasName: 'Other'
      },
      contact: {
        email: 'noreply@cybg.com',
        mobileCountryCode: '44',
        mobileNumber: '07711000000'
      },
      termsAndConditions: {
        termsAndConditionsId: '3520aa00-101a-4940-be15-3c963b6109a3',
        termsAndConditionsVersion: '1.0.0',
        acceptanceDateTime: '2022-07-01T17:00:00.000+01:00'
      },
      linkedProducts: {
        vRedId: '7770aa00-101a-4940-be15-3c963b6109b3',
        vAtlanticId: '6660aa00-101a-4940-be15-3c963b6109b3'
      },
      identifiers: {
        userId: '8800aa00-101a-4940-be15-3c963b6109b3',
        gpEcommerceId: '5550aa00-101a-4940-be15-3c963b6109b3'
      }
    },
    genericErrorStub: {
      errors: [{
        errorCode: 'CODE123',
        errorMessage: 'Some error occurred'
      }]
    },
    dupValErrorStub: {
      errors: [{
        errorCode: 'CONFLICT',
        errorMessage: 'Email is already assigned to another Wallet'
      }]
    }
  };
});

var index$g = /*#__PURE__*/Object.freeze({
  __proto__: null,
  createWallet: createWallet,
  getWallet: getWallet,
  getWalletSummary: getWalletSummary,
  getWalletCustomer: getWalletCustomer,
  getWalletContact: getWalletContact,
  getWalletTermsAndConditions: getWalletTermsAndConditions,
  getWalletTermsAndConditionsLatest: getWalletTermsAndConditionsLatest,
  getWalletLinkedProducts: getWalletLinkedProducts,
  updateWallet: updateWallet,
  updateWalletCustomer: updateWalletCustomer,
  updateWalletContact: updateWalletContact,
  updateWalletTermsAndConditions: updateWalletTermsAndConditions,
  updateWalletLinkedProducts: updateWalletLinkedProducts
});

var createPayMethodCardEndpoint="/wallet/:walletId/paymentMethods/card";var getPayMethodCardEndpoint="/wallet/:walletId/paymentMethods/card";var getPayMethodCardDetailsEndpoint="/wallet/:walletId/paymentMethods/card/:paymentMethodId";var getPaymethodCardBillingAddressEndpoint="/wallet/:walletId/paymentMethods/card/billingAddresses/distinct";var updatePayMethodCardEndpoint="/wallet/:walletId/paymentMethods/card/:paymentMethodId";var updatePayCardBillingAddressEndpoint="/wallet/:walletId/paymentMethods/card/:paymentMethodId/billingAddress";var updatePayCardLastUsedEndpoint="/wallet/:walletId/paymentMethods/card/:paymentMethodId/lastUsed";

/**
 * Gets new Wallet Details
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {import('types').TCreatePayCardPayload} createPayMethodCardPayload
 * @param {string} walletId
 */

function createPayMethodCard(_x, _x2, _x3, _x4, _x5) {
  return _createPayMethodCard.apply(this, arguments);
}

function _createPayMethodCard() {
  _createPayMethodCard = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, createPayMethodCardPayload, walletId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validatePayMethodCardPayload(createPayMethodCardPayload), validateWalletId(walletId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            path = buildUrl(createPayMethodCardEndpoint, {
              walletId: walletId
            });
            _context.next = 11;
            return apic.post(path, createPayMethodCardPayload);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));
  return _createPayMethodCard.apply(this, arguments);
}

/**
 * Gets Payment Method Card
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} walletId
 */

function getPayMethodCard(_x, _x2, _x3, _x4) {
  return _getPayMethodCard.apply(this, arguments);
}

function _getPayMethodCard() {
  _getPayMethodCard = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, walletId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateWalletId(walletId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            path = buildUrl(getPayMethodCardEndpoint, {
              walletId: walletId
            });
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));
  return _getPayMethodCard.apply(this, arguments);
}

/**
 * Get Wallet Card Details
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} walletId
 * @param {string} paymentMethodId
 */

function getPayMethodCardDetails(_x, _x2, _x3, _x4, _x5) {
  return _getPayMethodCardDetails.apply(this, arguments);
}

function _getPayMethodCardDetails() {
  _getPayMethodCardDetails = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, walletId, paymentMethodId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateWalletId(walletId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            path = buildUrl(getPayMethodCardDetailsEndpoint, {
              walletId: walletId,
              paymentMethodId: paymentMethodId
            });
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));
  return _getPayMethodCardDetails.apply(this, arguments);
}

/**
 * Gets payment method card billing address
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} walletId
 */

function getPayMethodCardBillingAddress(_x, _x2, _x3, _x4) {
  return _getPayMethodCardBillingAddress.apply(this, arguments);
}

function _getPayMethodCardBillingAddress() {
  _getPayMethodCardBillingAddress = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, walletId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateWalletId(walletId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            path = buildUrl(getPaymethodCardBillingAddressEndpoint, {
              walletId: walletId
            });
            _context.next = 11;
            return apic.get(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));
  return _getPayMethodCardBillingAddress.apply(this, arguments);
}

/**
 * Updates payment method card
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {import('types').TCardUpdatePayload} updatePayMethodPayload
 * @param {string} walletId
 * @param {string} paymentMethodId
 */

function updatePayMethodCard(_x, _x2, _x3, _x4, _x5, _x6) {
  return _updatePayMethodCard.apply(this, arguments);
}

function _updatePayMethodCard() {
  _updatePayMethodCard = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, updatePayMethodPayload, walletId, paymentMethodId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUpdatePayMethodCardPayload(updatePayMethodPayload), validateWalletId(walletId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            path = buildUrl(updatePayMethodCardEndpoint, {
              walletId: walletId,
              paymentMethodId: paymentMethodId
            });
            _context.next = 11;
            return apic.put(path, updatePayMethodPayload);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));
  return _updatePayMethodCard.apply(this, arguments);
}

/**
 * Updates payment card billing address
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {import('types').TBillingAddress} updatePayCardBillingAddPayload
 * @param {string} walletId
 * @param {string} paymentMethodId
 */

function updatePayCardBillingAddress(_x, _x2, _x3, _x4, _x5, _x6) {
  return _updatePayCardBillingAddress.apply(this, arguments);
}

function _updatePayCardBillingAddress() {
  _updatePayCardBillingAddress = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, updatePayCardBillingAddPayload, walletId, paymentMethodId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateUpdatePayCardBillingAddPayload(updatePayCardBillingAddPayload), validateWalletId(walletId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            path = buildUrl(updatePayCardBillingAddressEndpoint, {
              walletId: walletId,
              paymentMethodId: paymentMethodId
            });
            _context.next = 11;
            return apic.put(path, updatePayCardBillingAddPayload);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));
  return _updatePayCardBillingAddress.apply(this, arguments);
}

/**
 * Update payment card last used
 * @param {string} baseUrl
 * @param {import('types').BankId} bankId
 * @param {string} token
 * @param {string} walletId
 * @param {string} paymentMethodId
 */

function updatePayCardLastUsed(_x, _x2, _x3, _x4, _x5) {
  return _updatePayCardLastUsed.apply(this, arguments);
}

function _updatePayCardLastUsed() {
  _updatePayCardLastUsed = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(baseUrl, bankId, token, walletId, paymentMethodId) {
    var validations, apic, headers, path, resp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 5;
              break;
            }

            validations = validateCommonMethodParams(baseUrl, bankId, token, validateWalletId(walletId));

            if (!validations.error) {
              _context.next = 5;
              break;
            }

            return _context.abrupt("return", validations);

          case 5:
            apic = initApiConnector(baseUrl, bankId);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            path = buildUrl(updatePayCardLastUsedEndpoint, {
              walletId: walletId,
              paymentMethodId: paymentMethodId
            });
            _context.next = 11;
            return apic.put(path);

          case 11:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](0);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 19:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 15]]);
  }));
  return _updatePayCardLastUsed.apply(this, arguments);
}

var index$h = /*#__PURE__*/Object.freeze({
  __proto__: null,
  createPayMethodCard: createPayMethodCard,
  getPayMethodCard: getPayMethodCard,
  getPayMethodCardDetails: getPayMethodCardDetails,
  getPayMethodCardBillingAddress: getPayMethodCardBillingAddress,
  updatePayMethodCard: updatePayMethodCard,
  updatePayCardBillingAddress: updatePayCardBillingAddress,
  updatePayCardLastUsed: updatePayCardLastUsed
});

var getOrderEndpoint="/order/:vpayId";var getOrderSummaryEndpoint="/order/:vpayId/summary";

/**
 * Gets Order Details
 * @param {import('types').TWalletMethodParams} walletAPIParams
 *
 */

function getOrder(_x) {
  return _getOrder.apply(this, arguments);
}

function _getOrder() {
  _getOrder = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(_ref) {
    var baseUrl, token, _ref$vpayId, vpayId, validations, apic, headers, path, resp;

    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            baseUrl = _ref.baseUrl, token = _ref.token, _ref$vpayId = _ref.vpayId, vpayId = _ref$vpayId === void 0 ? '' : _ref$vpayId;
            _context.prev = 1;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 6;
              break;
            }

            validations = validateGPMethodParams({
              baseUrl: baseUrl,
              token: token,
              vpayId: vpayId
            });

            if (!validations.error) {
              _context.next = 6;
              break;
            }

            return _context.abrupt("return", validations);

          case 6:
            apic = initGPApiConnector(baseUrl);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            path = buildUrl(getOrderEndpoint, {
              vpayId: vpayId
            });
            _context.next = 12;
            return apic.get(path);

          case 12:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 16:
            _context.prev = 16;
            _context.t0 = _context["catch"](1);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 20:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[1, 16]]);
  }));
  return _getOrder.apply(this, arguments);
}

/**
 * Gets Order Summary Details
 * @param {import('types').TWalletMethodParams} walletAPIParams
 */

function getOrderSummary(_x) {
  return _getOrderSummary.apply(this, arguments);
}

function _getOrderSummary() {
  _getOrderSummary = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(_ref) {
    var baseUrl, token, _ref$vpayId, vpayId, validations, apic, headers, path, resp;

    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            baseUrl = _ref.baseUrl, token = _ref.token, _ref$vpayId = _ref.vpayId, vpayId = _ref$vpayId === void 0 ? '' : _ref$vpayId;
            _context.prev = 1;

            if (!(process.env.NODE_ENV !== 'production')) {
              _context.next = 6;
              break;
            }

            validations = validateGPMethodParams({
              baseUrl: baseUrl,
              token: token,
              vpayId: vpayId
            });

            if (!validations.error) {
              _context.next = 6;
              break;
            }

            return _context.abrupt("return", validations);

          case 6:
            apic = initGPApiConnector(baseUrl);
            headers = setAppVersionHeaders(apic.headers);
            apic.setHeaders(_objectSpread(_objectSpread({}, headers), {}, {
              Authorization: "Bearer " + token
            }));
            path = buildUrl(getOrderSummaryEndpoint, {
              vpayId: vpayId
            });
            _context.next = 12;
            return apic.get(path);

          case 12:
            resp = _context.sent;
            return _context.abrupt("return", parseResponse(resp));

          case 16:
            _context.prev = 16;
            _context.t0 = _context["catch"](1);
            log.logError(_context.t0);
            return _context.abrupt("return", endpointError(_context.t0));

          case 20:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[1, 16]]);
  }));
  return _getOrderSummary.apply(this, arguments);
}

var index$i = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getOrder: getOrder,
  getOrderSummary: getOrderSummary
});

export { index as accountService, index$1 as addressSearch, index$2 as beneficiaryManagement, index$3 as caseService, index$c as customerAssist, index$4 as customerService, index$i as gpOrderService, index$g as gpWalletService, index$5 as insightManagement, index$d as oauthService, index$b as recordManagement, index$6 as referenceDataService, index$7 as secureMessaging, index$e as securityDevices, index$8 as userAuth, index$f as userConfirmation, index$9 as userService, index$a as userVerification, index$h as walletPaymethodCardService };
